import { useEffect, useState } from "react";
import { Loading } from "../../Loading/Index";
import { parseUtcDate } from "../../../utils/date/formatedDate";
import { ReactComponent as ArrowIcon } from "../../../assets/svg/icons/rightChevron.svg";
import { ReactComponent as InfoIcon } from "../../../assets/svg/icons/info.svg";
import { useTableMonitor } from "../../../hooks/useTableMonitor";
import ViewTasks from "../../Tasks/ViewTasks";
import "../TaskEvaluation/styles.css";
import { useTranslation } from "react-i18next";
import { TranslatedHeader } from "../TranslatedHeader";
import { translateMonitoringTask } from "../../../i18n/translateMonitoringTask";

type NotificationTableProps = {
  listTable: ITaskNotification[];
  isLoading: boolean;
};

type Column = {
  key: string;
  label: string | JSX.Element;
};

function formatDateTime(value?: string | null) {
  const date = parseUtcDate(value);
  if (!date || Number.isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const time = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);

  return `${day}/${month}/${year} - ${time}`;
}

function getTaskTypeName(taskType: string) {
  try {
    const taskTypeEnumObj = JSON.parse(
      localStorage.getItem("TaskTypeEnumObj") || "{}",
    );

    return taskTypeEnumObj[taskType] || taskType;
  } catch (e) {
    return taskType;
  }
}

function getStatusKey(status: string) {
  const statusMap: Record<string, string> = {
    SENT: "sent",
    PENDING: "pending",
    CONVERTED: "converted",
    CANCELLED: "cancelled",
    CANCELED: "cancelled",
  };

  return statusMap[status];
}

export default function NotificationTable({
  listTable,
  isLoading,
}: NotificationTableProps) {
  const { t } = useTranslation();
  const columns: Column[] = [
    { key: "userName", label: t("tables.user") },
    { key: "taskType", label: t("tables.task") },
    { key: "microAreaManagementGroupInitials", label: t("tables.managementGroup") },
    { key: "status", label: t("tables.status") },
    { key: "createdAt", label: <TranslatedHeader value={t("tables.creationDate")} /> },
    { key: "convertedAt", label: <TranslatedHeader value={t("tables.synchronizationDate")} /> },
  ];
  const [selectedId, setSelectedId] = useState(0);
  const { selectedOrder, setSelectedOrder, sortAsc, setSortAsc } =
    useTableMonitor();

  useEffect(() => {
    setSelectedOrder("createdAt");
  }, [setSelectedOrder]);

  if (!listTable.length) {
    return isLoading ? (
      <div className="emptyTable_container">
        <Loading visible={true} />
      </div>
    ) : (
      <div className="emptyTable_container">
        <span>{t("commonUi.noResults")}</span>
      </div>
    );
  }

  return (
    <>
      <table className="tableTaskEvaluation" style={{ minWidth: 1100 }}>
        <thead>
          <tr>
            {columns.map(({ key, label }) => (
              <th
                key={key}
                onClick={() => {
                  setSelectedOrder(key);
                  if (selectedOrder !== key) {
                    setSortAsc(false);
                    return;
                  }
                  setSortAsc(!sortAsc);
                }}
              >
                <div className="infoColumn">
                  {label}
                  <ArrowIcon
                    className={`iconSort${sortAsc ? "--asc" : ""}`}
                    style={{
                      display: selectedOrder === key ? "block" : "none",
                    }}
                  />
                </div>
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {listTable.map((notification) => (
            <tr key={notification.id}>
              <td>{notification.user_name || ""}</td>
              <td>{translateMonitoringTask(getTaskTypeName(notification.task_type))}</td>
              <td>{notification.micro_area_management_group_initials || ""}</td>
              <td>{getStatusKey(notification.status) ? t(`notifications.statuses.${getStatusKey(notification.status)}`) : notification.status}</td>
              <td>{formatDateTime(notification.created_at)}</td>
              <td>{formatDateTime(notification.converted_at)}</td>
              <td className="infoButton">
                <button
                  title={t("commonUi.viewDetails")}
                  style={{
                    display: notification.converted_task_id ? "block" : "none",
                    backgroundColor: "transparent",
                  }}
                >
                  <InfoIcon
                    onClick={() => {
                      if (notification.converted_task_id) {
                        setSelectedId(notification.converted_task_id);
                      }
                    }}
                  />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {isLoading && (
        <div className="tableLoadingOverlay" style={{ minWidth: 1100 }}>
          <div className="lds-ring">
            <div></div>
            <div></div>
          </div>
        </div>
      )}

      <ViewTasks selected={selectedId} setSelectedId={setSelectedId} />
    </>
  );
}
